import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Col, Card, CardBody, Spinner } from 'reactstrap';
import './applicationsWidget.css';

const ELFSIGHT_SCRIPT_ID = 'elfsight-platform-script';
const ELFSIGHT_SCRIPT_SRC = 'https://elfsightcdn.com/platform.js';
const ELFSIGHT_LOAD_TIMEOUT_MS = 15000;

const loadElfsightScript = () => {
  const existingScript = document.getElementById(ELFSIGHT_SCRIPT_ID);

  if (existingScript) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = ELFSIGHT_SCRIPT_ID;
    script.src = ELFSIGHT_SCRIPT_SRC;
    script.async = true;
    script.onload = resolve;
    script.onerror = resolve;
    document.body.appendChild(script);
  });
};

const hasElfsightContent = (container) => {
  if (!container) {
    return false;
  }

  if (container.querySelector('iframe')) {
    return true;
  }

  return Array.from(container.children).some((child) => child.offsetHeight > 50);
};

const ApplicationsWidget = () => {
  const history = useHistory();
  const widgetRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);

  useEffect(() => {
    if (selectedOption !== 'whitelabel') {
      setIsWidgetLoading(false);
      return undefined;
    }

    setIsWidgetLoading(true);

    let observer;
    let timeoutId;
    let isCancelled = false;

    const markWidgetReady = () => {
      if (!isCancelled) {
        setIsWidgetLoading(false);
      }
    };

    const watchForWidgetContent = () => {
      const container = widgetRef.current;

      if (hasElfsightContent(container)) {
        markWidgetReady();
        return;
      }

      observer = new MutationObserver(() => {
        if (hasElfsightContent(widgetRef.current)) {
          markWidgetReady();
          observer.disconnect();
        }
      });

      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }

      timeoutId = window.setTimeout(() => {
        markWidgetReady();
        if (observer) {
          observer.disconnect();
        }
      }, ELFSIGHT_LOAD_TIMEOUT_MS);
    };

    loadElfsightScript().then(() => {
      if (!isCancelled) {
        watchForWidgetContent();
      }
    });

    return () => {
      isCancelled = true;
      if (observer) {
        observer.disconnect();
      }
      window.clearTimeout(timeoutId);
    };
  }, [selectedOption]);

  const handleWhitelabelClick = () => {
    setSelectedOption('whitelabel');
  };

  const handleRestApiClick = () => {
    history.push('/rest-api');
  };

  return (
    <Col xl="12">
      <Card>
        <CardBody>
          {selectedOption === 'whitelabel' ? (
            <div className="applicationsWidgetWidgetContainer">
              {isWidgetLoading && (
                <div className="applicationsWidgetWidgetLoading" aria-live="polite" aria-busy="true">
                  <Spinner color="primary" className="applicationsWidgetWidgetSpinner" />
                </div>
              )}
              <div
                ref={widgetRef}
                className="elfsight-app-a5cc63fc-daed-4707-8fcd-8ba179e3f393"
                data-elfsight-app-lazy
              />
            </div>
          ) : (
            <div className="applicationsWidgetContainer">
              <h3 className="applicationsWidgetHeading">What are you interested in?</h3>
              <p className="applicationsWidgetSubheading">Choose an option below to explore our solutions</p>

              <div className="applicationsWidgetOptionsGrid">
                <button
                  type="button"
                  className={`applicationsWidgetOptionCard applicationsWidgetWhitelabel`}
                  onClick={handleWhitelabelClick}
                >
                  <div className="applicationsWidgetIconWrap">
                    <i className={`mdi mdi-view-dashboard applicationsWidgetIcon`} aria-hidden="true" />
                  </div>
                  <h4 className="applicationsWidgetTitle">I am interested in Whitelabel</h4>
                  <p className="applicationsWidgetDescription">
                    Launch your own branded music distribution platform
                  </p>
                  <span className="hint">Explore whitelabel</span>
                </button>

                <button
                  type="button"
                  className={`applicationsWidgetOptionCard applicationsWidgetRestApi`}
                  onClick={handleRestApiClick}
                >
                  <div className="applicationsWidgetIconWrap">
                    <i className={`mdi mdi-lan-connect applicationsWidgetIcon`} aria-hidden="true" />
                  </div>
                  <h4 className="applicationsWidgetTitle">I am interested in REST API</h4>
                  <p className="applicationsWidgetDescription">
                    Build custom integrations and automate workflows in your existing stack
                  </p>
                  <span className="applicationsWidgetHint">View REST API</span>
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default ApplicationsWidget;
